import { mergeClasses, Tooltip } from '@fluentui/react-components'
import { Cursor20Filled, Cursor20Regular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { useContextSourceBadgeStyles } from './contextSourceBadge.styles'

/**
 * Non-interactive visualization of whether a Word mouse selection is available
 * as AI context. Selection itself is owned by Word - this badge only reflects state.
 *
 * Uses `activeStatic` (not `active`) so there is no hover/press color change -
 * a clickable look would imply the user can toggle selection from the row.
 * Cursor20 matches Document20 / CommentMultiple20 on the sibling badges.
 */
interface MouseSelectionBadgeProps {
  hasMouseSelection: boolean
}

export const MouseSelectionBadge = ({ hasMouseSelection }: MouseSelectionBadgeProps) => {
  const classes = useContextSourceBadgeStyles()
  const { t } = useTranslation()

  const label = hasMouseSelection ? t('contextBar.mouseActive') : t('contextBar.mouseDisabled')

  return (
    <Tooltip content={label} relationship="label" appearance="inverted">
      <span
        className={mergeClasses(
          classes.badge,
          hasMouseSelection ? classes.activeStatic : classes.disabled
        )}
        role="img"
        aria-label={label}
        data-testid="mouse-selection-badge"
        data-state={hasMouseSelection ? 'active' : 'disabled'}>
        {hasMouseSelection ? (
          <Cursor20Filled aria-hidden={true} data-testid="mouse-selection-icon-active" />
        ) : (
          <Cursor20Regular aria-hidden={true} data-testid="mouse-selection-icon-disabled" />
        )}
      </span>
    </Tooltip>
  )
}
